import React from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    ContentContainer,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {ORDER_STATUS_COLORS} from 'core/constants/statuses';
import {OrderStatus, OrderTypes} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {commonDataState$, Gate} from 'models/orders/detail';
import {profileCountries$} from 'models/profile/common';
import {colors} from 'theme';
import {Country} from 'types/gtins';
import {OrderDetailFormatted, OrderDetailRaw} from 'types/orders';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Products} from './Products';
import {DetailsWrapper, Divider, Error, ErrorsWrapper, Subtitle} from './styled';

function getStatusColors(status?: OrderStatus) {
    return {color: ORDER_STATUS_COLORS[status ?? 'DEFAULT']};
}

export function convertData(
    data: OrderDetailRaw | null,
    t: TFunction,
    language?: string,
    profileCountries?: Country[],
): OrderDetailFormatted {
    return {
        orderId: handleNil(data?.orderId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        status: handleNil(data?.status, (value: OrderStatus) => t(`dictionaries.orders.${value}`)),
        totalMarkingCodes: handleNil(data?.totalMarkingCodes),
        manufacturingSite: handleNil(data?.manufacturingSite),
        country: handleNil(data?.country, (country: string) => {
            const countryData = profileCountries?.find((c) => c.country === country);
            return countryData && language
                ? language === 'en'
                    ? countryData.country
                    : countryData.countryFr
                : country;
        }),
        manufacturingLine: handleNil(data?.manufacturingLine),
        availableCodes: handleNil(data?.availableCodes),
        errors: data?.errors
            ? data.errors.map(
                  (err) => `${t('pages.orders.detail.error')} #${err.code}. ${err.message}`,
              )
            : undefined,
        manufacturerName: handleNil(data?.manufacturerName),
        manufacturerAddress: handleNil(data?.manufacturerAddress),
        orderType: handleNil(data?.orderType, (value: OrderTypes) =>
            t(`dictionaries.typeByDomestic.${value}`),
        ),
    };
}

export const OrdersDetailPage = () => {
    const {t, i18n} = useTranslation();
    const {language} = i18n;
    const profileCountries = useStore(profileCountries$);
    const {id}: {id: string} = useParams();
    const {data, error, isLoading} = useStore(commonDataState$);
    useGate(Gate, {id});
    const copy = useClipboard();

    const mappedData = React.useMemo(() => convertData(data, t, language, profileCountries), [
        data,
        t,
        language,
        profileCountries,
    ]);

    const copyCode = () => {
        copy(mappedData.orderId);
        toast.success(t('pages.orders.detail.idCopied'));
    };

    return (
        <>
            <Page title={t('pages.orders.detail.orderTitle')}>
                <BackTo to={ROUTE.ORDERS_GRID}>{t('pages.orders.back')}</BackTo>
                <ContentContainer
                    status={isLoading ? 'loading' : error != null ? 'error' : 'default'}
                >
                    <TitleWrapper extraStyles="justify-content: flex-start;">
                        <TitleText>
                            <Title>
                                {t('pages.orders.detail.orderId')} <Id>{mappedData.orderId}</Id>
                            </Title>
                            <CopyButton
                                icon={
                                    <Icon
                                        source={CopyIcon}
                                        width={15}
                                        height={15}
                                        fillColor={colors.brandPrimary}
                                    />
                                }
                                onClick={copyCode}
                                hoverIndication={true}
                                label={t('pages.orders.detail.common.copyId')}
                                labelPlace={'bottom'}
                            />
                        </TitleText>
                        <DetailsWrapper>
                            {/*<Button onClick={onDownloadXLS}>*/}
                            {/*    {t('pages.orders.detail.common.download')}*/}
                            {/*</Button>*/}
                            <StatusWrapper
                                style={{flexShrink: 0}}
                                {...getStatusColors(data?.status)}
                            >
                                {mappedData.status}
                            </StatusWrapper>
                        </DetailsWrapper>
                    </TitleWrapper>
                    {mappedData.errors && (
                        <ErrorsWrapper>
                            {mappedData.errors.map((err, index) => (
                                <Error key={index}>{err}</Error>
                            ))}
                        </ErrorsWrapper>
                    )}
                    <Subtitle>{t('pages.orders.detail.commonInfo')}</Subtitle>
                    <ContentColumns
                        data={[
                            {
                                createdAt: mappedData.createdAt,
                                ...(data?.orderType === OrderTypes.LOCAL && {
                                    manufacturerSite: mappedData.manufacturingSite,
                                }),
                                manufacturerAddress: mappedData.manufacturerAddress,
                            },
                            {
                                orderType: mappedData.orderType,
                                ...(data?.orderType === OrderTypes.LOCAL && {
                                    manufacturerLine: mappedData.manufacturingLine,
                                }),
                            },
                            {
                                countryOfOrigin: mappedData.country,
                                manufacturerName: mappedData.manufacturerName,
                            },
                        ]}
                        tNameSpace="pages.orders.detail"
                    />
                    <Divider />
                    <Subtitle>{t('pages.orders.detail.products')}</Subtitle>
                    <Products />

                    {/* <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('pages.orders.detail.commonInfo')}</RouterTab>
                        <RouterTab url="products">{t('pages.orders.detail.products')}</RouterTab>
                    </RouterTabList>

                    <RouterSwitch rootPath={path}>
                        <Route path={`${path}`} exact>
                            <ContentColumns
                                data={[
                                    {country: mappedData.country},
                                    {
                                        factoryId: mappedData.factoryId,
                                        productionLineId: mappedData.productionLineId,
                                    },
                                ]}
                                tNameSpace="pages.orders.detail.common"
                            />
                        </Route>
                        <Route path={`${path}/products`} exact>
                            <Products />
                        </Route>
                    </RouterSwitch> */}
                </ContentContainer>
            </Page>
        </>
    );
};
